import React from 'react';
import { RefreshCw } from 'lucide-react';
import { useVeniceBalance } from '../hooks/useVeniceBalance';
import { describeVeniceBalance, spendableUsd } from '../utils/venice';

/**
 * The balance stands in a line of small faint text (a node's price line): a text-only action in
 * the colour and weight of that line, underlined when pointed at.
 */
const BALANCE_LINK = 'ns-link font-medium text-ink-faint hover:text-ink';

/**
 * The Venice balance next to a price: "$12.34 left". It turns to the warning colour when the
 * estimate is more than is left, or nothing can be spent. Click to read it again.
 */
export const VeniceBalanceLabel: React.FC<{ apiKey?: string; estimateUsd?: number | null }> = ({ apiKey, estimateUsd }) => {
    const { balance, error, loading, refresh } = useVeniceBalance(apiKey);
    if (!apiKey) return null;
    if (error) {
        return (
            <button type="button" onClick={refresh} title={`${error} Click to try again.`} className={BALANCE_LINK} data-venice-balance="error">
                balance unavailable
            </button>
        );
    }
    if (!balance) return loading ? <span className="text-ink-faint" data-venice-balance="loading">reading balance...</span> : null;
    const { short, full } = describeVeniceBalance(balance);
    const spendable = spendableUsd(balance);
    const tooLittle = estimateUsd != null && spendable !== null && estimateUsd > spendable;
    const warn = !balance.canConsume || tooLittle;
    return (
        <button
            type="button"
            onClick={refresh}
            title={`Venice balance: ${full}.${tooLittle ? ' The estimate is more than is left.' : ''} Click to read it again.`}
            className={warn ? 'ns-link text-warn-deep' : BALANCE_LINK}
            data-venice-balance={warn ? 'low' : 'ok'}
        >
            {tooLittle && balance.canConsume ? `only ${short}` : short}
        </button>
    );
};

/** The balance in the settings, under the Venice key: everything the account holds, and what it is paid from. */
export const VeniceBalanceLine: React.FC<{ apiKey?: string }> = ({ apiKey }) => {
    const { balance, error, loading, refresh } = useVeniceBalance(apiKey);
    if (!apiKey) return null;
    const paidFrom: Record<string, string> = { USD: 'USD', VCU: 'VCU', DIEM: 'staked DIEM', BUNDLED_CREDITS: 'bundled credits', EARNED_CREDITS: 'earned credits' };
    return (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-xs" data-venice-balance-line>
            <span className="font-semibold text-ink-soft">Balance:</span>
            {error ? (
                <span className="text-danger-deep">{error}</span>
            ) : balance ? (
                <>
                    <span className={balance.canConsume ? 'text-ink-soft' : 'text-danger-deep font-semibold'}>{describeVeniceBalance(balance).full}</span>
                    {balance.currency && <span className="text-ink-faint">Requests are paid from {paidFrom[balance.currency]}.</span>}
                    {!balance.canConsume && <span className="text-danger-deep">Top up at venice.ai to keep generating.</span>}
                </>
            ) : (
                <span className="text-ink-faint">{loading ? 'Reading...' : 'Not read yet.'}</span>
            )}
            {/* The smallest button, flattened to the height of the line of text it stands in */}
            <button type="button" onClick={refresh} disabled={loading} className="ns-btn ns-btn-ghost ns-btn-xs ns-btn-flat px-2.5 py-0.5" aria-label="Read the Venice balance again">
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
        </span>
    );
};
